import { Alert, AlertProps, Button, Col, message, Row, Select, Space, Typography } from 'antd';
import { FC, useEffect, useState } from 'react';
import { AnalysisConfig, PeptideSet } from '../types/analysisTypes';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { resetAnalysisConfig } from '../features/analysisConfig/analysisConfigSlice';
import { TableSkeleton } from '../components/table_skeleton';
import SectionPage from '../components/section/SectionPage';
import { getPeptideSetType, renderPeptideSetType } from '../compute/utils';
import { fetchAuthorizedAPIEndpoint, fetchAuthorizedWebappAPIEndpoint, getUserSub, useQueryParam } from '../utils';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Header } from 'antd/lib/layout/layout';
import { useMediaQuery } from '../App';
import { selectVisibleDeleteRuns, setVisibleDeleteRuns } from '../features/analysisConfig/sessionInfoSlice';
import { FeatureFlag, UserClaimsWithTSDB } from '../types/userType';
import { RunMetadata, SelectedRun, SourceType, TSDBRef } from '../types/runsType';
import { WebappRecord } from '../types/webappTypes';
import { RecordsTable } from '../components/records_table';

export const WebappDBSelectorComponent: FC = () => {
  const [runsMap, setRunsMap] = useState<Record<TSDBRef['Name'], RunMetadata[]>>({});
  const [records, setRecords] = useState<WebappRecord[]>([]);
  const [selectedRecords, setSelectedRecords] = useState<string[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [isRecordsListLoading, setIsRecordsListLoading] = useState(true);
  const [isTransferringHandle, setIsTransferringHandle] = useState<boolean>(false);
  const [retryCounter, setRetryCounter] = useState<number>(0);
  const [selectedRecordsMap, setSelectedRecordsMap] = useState<Record<TSDBRef['Name'], SelectedRun[]>>({});
  const [userInfo, setUserInfo] = useState<UserClaimsWithTSDB | null>(null);
  const [selectedDeleteRuns, setSelectedDeleteRuns] = useState<string[]>([]);

  const [startParam, setStartParam] = useQueryParam('start', '-2w');

  const visibleDeleteRuns = useAppSelector<boolean>(selectVisibleDeleteRuns);

  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [accessToken, setAccessToken] = useState<string | undefined>();
  const isRowBased = useMediaQuery('(max-width: 768px)');

  const chosenTSDBRef: TSDBRef = { Name: 'Aryballe-Web-App', URL: '', Token: '', Org: '' };
  const isCollapsed: boolean = true;

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!user || !isAuthenticated) setUserInfo(null);
    else setUserInfo(user as UserClaimsWithTSDB);
  }, [isAuthenticated, user]);

  useEffect(() => {
    getAccessTokenSilently().then((token) => {
      setAccessToken(token);
    });
  }, [getAccessTokenSilently]);

  useEffect(() => {
    dispatch(resetAnalysisConfig());
    if (userInfo === null) {
      return;
    }
    setIsRecordsListLoading(true);

    fetchAuthorizedWebappAPIEndpoint(`/records?start=${startParam}`, accessToken)
      .then((resp) => {
        if (resp.ok) {
          // console.log(resp.json());
          return resp.json();
        } else {
          throw new Error();
        }
      })
      .then((_records: WebappRecord[]) => {
        if (_records === null) {
          _records = [];
        }
        setRecords(_records);
        console.log('recordMap:', _records);
        setIsError(false);
        setIsRecordsListLoading(false);
      })
      .catch((e: Error) => {
        setIsError(true);
        setIsRecordsListLoading(false);
      });
  }, [userInfo, retryCounter, startParam]);

  useEffect(() => {
    if (selectedDeleteRuns.length === 0) setVisibleDeleteRuns(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (userInfo === null) {
    // Case of still loading the page
    return null;
  }

  const isDisabled = () => {
    return selectedRecords.length === 0;
  };

  const handleContinue = () => {
    if (userInfo === null) {
      return;
    }

    setIsTransferringHandle(true);

    fetchAuthorizedWebappAPIEndpoint(`/init/doh/session?record_ids=${selectedRecords.join(',')}`, accessToken)
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        } else {
          throw resp.json();
        }
      })
      .catch((e) => {
        setIsTransferringHandle(false);
        e.then((resp: { Reason: string }) => {
          message.error(`Error occured during data digestion in the cloud: ${resp.Reason}`, 5);
        });
        return;
      })
      .then((analysisConfig: AnalysisConfig) => {
        dispatch(resetAnalysisConfig());
        window.open(`/dashboard/${analysisConfig.SessionID}`, '_blank');
        setIsTransferringHandle(false);
      });
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Col style={{ width: visibleDeleteRuns ? 'calc(100% - 300px)' : '100%' }}>
          <SectionPage backgroundColor={undefined} marginBottom={120}>
            <Row justify={'end'}>
              <Col style={{ marginBottom: 20, marginTop: -24 }}>
                <span>Preload experiments for: </span>
                <Select
                  style={{ minWidth: '150px', marginTop: '10px' }}
                  onChange={(value) => {
                    setStartParam(value);
                  }}
                  value={startParam}
                >
                  <Select.Option key="all-time" value="0">
                    All-time
                  </Select.Option>
                  <Select.Option key="-12mo" value="-12mo">
                    Last year
                  </Select.Option>
                  <Select.Option key="-6mo" value="-6mo">
                    Last semester
                  </Select.Option>
                  <Select.Option key="-3mo" value="-3mo">
                    Last quarter
                  </Select.Option>
                  <Select.Option key="-2w" value="-2w">
                    Last two weeks
                  </Select.Option>
                </Select>
              </Col>
            </Row>
            {isRecordsListLoading ? (
              <TableSkeleton />
            ) : (
              <>
                <Row>
                  <Col span={24}>
                    <RecordsTable records={records} selectedRecords={selectedRecords} setSelectedRecords={setSelectedRecords} selectedDeleteRuns={selectedDeleteRuns} setSelectedDeleteRuns={setSelectedDeleteRuns} />
                  </Col>
                </Row>
              </>
            )}
          </SectionPage>
          <Row>
            <Header
              style={{
                background: 'white',
                padding: '10px 30px',
                position: 'fixed',
                bottom: isRowBased ? 48 : 0,
                lineHeight: '10px',
                minHeight: 93,
                left: isRowBased ? '0px' : isCollapsed ? '60px' : '240px',
                width: isRowBased ? '100%' : `calc(100% - (${isCollapsed ? '60px' : '240px'} + ${visibleDeleteRuns ? '300px' : '0px'}))`,
                zIndex: 2,
                boxShadow: '1px -1px 4px #00000010',
              }}
            >
              {!isRecordsListLoading && (
                <>
                  <Col>
                    <Row align="top" style={{ marginBottom: 10 }}>
                      <Typography.Text
                        copyable={{
                          icon: [<CloseOutlined style={{ color: 'red' }} />, <CheckOutlined />],
                          tooltips: ['Unselect all records', ''],
                          onCopy: (e) => {
                            setSelectedRecords([]);
                          },
                        }}
                      >
                        Selected <b>{selectedRecords.length}</b> records
                      </Typography.Text>
                    </Row>
                    <Row justify="center" style={{ marginTop: '0px' }}>
                      <Col xs={24}>
                        <Button onClick={handleContinue} size="large" style={{ width: '100%', borderRadius: 5 }} type="primary" disabled={isDisabled()} loading={isTransferringHandle}>
                          Continue
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </>
              )}
            </Header>
          </Row>
        </Col>
      </div>
    </>
  );
};

export const WebappDBPage: FC = () => {
  return <WebappDBSelectorComponent />;
};
